/**
 * Client-side IFC sniff (docs/ORG_TENANCY_SPEC.md, Upload UX): the
 * browser holds the COMPLETE file at t=0, so validation and the class
 * histogram cost nothing extra -- reject unsupported schemas BEFORE a
 * byte uploads, and give the ingest progress bar an honest denominator.
 * STEP files put FILE_SCHEMA in the header's first kilobyte; entity
 * lines (#n=IFCCLASS(...) are lexically self-contained, so a streaming
 * regex scan yields the histogram without parsing.
 */

export interface IfcSniff {
  schema: string | null;
  totalEntities: number;
  classCounts: [string, number][]; // sorted desc
}

// Mirrors the server's supported set (scripts/check_ifc_schema_versions.py).
const SUPPORTED_SCHEMAS = new Set(['IFC4', 'IFC2X3', 'IFC4X3']);

export function isSupportedSchema(schema: string | null): boolean {
  return schema !== null && SUPPORTED_SCHEMAS.has(schema.toUpperCase());
}

const ENTITY_RE = /#\d+\s*=\s*(IFC[A-Z0-9]+)\s*\(/g;
// Longest IFC class name is well under this; the carry window keeps a
// match from being split across chunk boundaries.
const CARRY = 128;

export async function sniffIfc(file: File): Promise<IfcSniff> {
  const head = await file.slice(0, 4096).text();
  const schemaMatch = head.match(/FILE_SCHEMA\s*\(\s*\(\s*'([^']+)'/);
  const schema = schemaMatch ? schemaMatch[1] : null;

  const counts = new Map<string, number>();
  const tally = (text: string) => {
    ENTITY_RE.lastIndex = 0;
    for (let m = ENTITY_RE.exec(text); m !== null; m = ENTITY_RE.exec(text)) {
      const cls = m[1];
      counts.set(cls, (counts.get(cls) ?? 0) + 1);
    }
  };

  const reader = file.stream().pipeThrough(new TextDecoderStream()).getReader();
  let carry = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = carry + value;
    const split = Math.max(chunk.length - CARRY, 0);
    tally(chunk.slice(0, split));
    carry = chunk.slice(split);
  }
  tally(carry);

  const classCounts = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  const totalEntities = classCounts.reduce((sum, [, n]) => sum + n, 0);
  return { schema, totalEntities, classCounts };
}
